/*
 * Scanning the seal with the tablet camera.
 *
 * Tablets that can read a barcode do it themselves, through the browser's BarcodeDetector. Where
 * that is missing, or the camera is refused, the code can be typed in from under the barcode
 * instead, so a broken camera never stops a medicines round.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { ApiError, api } from './api.ts';
import { Overlay } from './overlay.tsx';

type Answer = { ok: boolean; problem: string | null; text: string };

/* The browser's own barcode reader, where the tablet has one. */
type Detector = { detect: (source: CanvasImageSource) => Promise<Array<{ rawValue: string }>> };
type WithDetector = { BarcodeDetector?: new (options: { formats: string[] }) => Detector };

export function SealScan({
  residentId,
  dueOn,
  dueAt,
  roundName,
  onClose,
  onGood,
}: {
  residentId: string;
  dueOn: string;
  dueAt: string;
  roundName: string;
  onClose: () => void;
  onGood: (text: string) => void;
}) {
  const video = useRef<HTMLVideoElement | null>(null);
  const [answer, setAnswer] = useState<Answer | null>(null);
  const [typed, setTyped] = useState('');
  const [camera, setCamera] = useState<'starting' | 'on' | 'unavailable'>('starting');
  const [busy, setBusy] = useState(false);

  const check = useCallback(
    async (code: string) => {
      if (busy) return;
      setBusy(true);
      try {
        const result = await api.post<Answer>('/api/scan/tray', { code, residentId, dueOn, dueAt });
        setAnswer(result);
        if (result.ok) onGood(result.text);
      } catch (err) {
        setAnswer({
          ok: false,
          problem: 'unreadable',
          text: err instanceof ApiError ? err.message : 'Could not check that seal.',
        });
      } finally {
        setBusy(false);
      }
    },
    [busy, residentId, dueOn, dueAt, onGood],
  );

  useEffect(() => {
    const detectorType = (window as unknown as WithDetector).BarcodeDetector;
    if (!detectorType || !navigator.mediaDevices?.getUserMedia) {
      setCamera('unavailable');
      return;
    }

    let stream: MediaStream | null = null;
    let timer: number | null = null;
    let stopped = false;
    const detector = new detectorType({ formats: ['qr_code'] });

    const start = async () => {
      try {
        stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'environment' } });
        if (stopped) return;
        if (video.current) {
          video.current.srcObject = stream;
          await video.current.play();
        }
        setCamera('on');
        timer = window.setInterval(async () => {
          if (!video.current || stopped) return;
          try {
            const found = await detector.detect(video.current);
            const code = found[0]?.rawValue;
            if (code) {
              window.clearInterval(timer ?? 0);
              await check(code);
            }
          } catch {
            /* a frame that would not read; the next one will */
          }
        }, 400);
      } catch {
        setCamera('unavailable');
      }
    };

    void start();
    return () => {
      stopped = true;
      if (timer) window.clearInterval(timer);
      stream?.getTracks().forEach((track) => track.stop());
    };
  }, [check]);

  return (
    <Overlay onClose={onClose}>
      <div className="card wide">
        <h2>Scan the seal</h2>
        <p className="muted">
          Hold the barcode on the {roundName.toLowerCase()} tray up to the camera. It checks the tray is for
          this resident, this time of day and this cycle.
        </p>

        {camera === 'on' ? (
          <div className="scanner">
            <video ref={video} playsInline muted />
          </div>
        ) : null}

        {camera === 'unavailable' ? (
          <>
            <p className="notice">
              This tablet cannot use its camera here. Type the code printed under the barcode instead.
            </p>
            <label htmlFor="typed-code">Code on the seal</label>
            <input id="typed-code" value={typed} onChange={(e) => setTyped(e.target.value)}
              placeholder="AYPT|a1b2c3d4|morning|260928|1" />
            <button type="button" disabled={!typed.trim() || busy} onClick={() => void check(typed.trim())}>
              Check it
            </button>
          </>
        ) : null}

        {answer ? (
          <p className={`notice ${answer.ok ? '' : 'warn'}`} role="alert">
            {answer.text}
          </p>
        ) : null}

        <div className="row-actions">
          {answer && !answer.ok ? (
            <button type="button" onClick={() => setAnswer(null)}>Try another tray</button>
          ) : null}
          <button type="button" className="quiet" onClick={onClose}>
            {answer?.ok ? 'Carry on' : 'Close'}
          </button>
        </div>
      </div>
    </Overlay>
  );
}
